import { Link, Navigate } from "react-router-dom";
import { useClients, useUnreadMessages, useAccountStatus, useFamilyBilling, FamilyClientListItem } from "../lib/queries";
import { AthleteStatus } from "../components/AthleteStatus";
import { LoadingState, SkeletonList, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import { SkaterLoginForm } from "../components/SkaterLoginForm";
import { TourPrompt } from "../components/TourPrompt";
import { SkaterLoginDetails } from "../components/SkaterLoginDetails";
import { initials } from "../lib/initials";

/**
 * The one-line version of what this family pays, for the top of their own dashboard.
 *
 * Cents and an explicit currency because that is how the billing summary reports it -- formatting it
 * here rather than trusting a stored display string keeps this honest if the currency ever changes.
 */
function describeMonthlyTotal(cents: number, currency: string | null): string {
  const amount = (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: currency ?? "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
  return `${amount} a month`;
}

/** Family home: a guardian's dependents (profile switcher), or a minor's own single profile (PRD §2.3/§5.2). */
export function FamilyHome() {
  const clientsQuery = useClients<FamilyClientListItem[]>();
  // Same cache as the nav dot: which of this family's athletes has a conversation waiting.
  const unread = useUnreadMessages();
  const unreadClientIds = new Set(unread.data?.client_ids ?? []);

  /**
   * Whether the person reading this is the athlete rather than the guardian.
   *
   * `not_applicable` means a login with no account of its own, which is exactly a minor's own login --
   * the same fact the account gate reads (architecture §2c). Free to ask for: this query is already in
   * cache from the gate that rendered this route.
   */
  const isAthleteLogin = useAccountStatus().data?.access === "not_applicable";
  const onlyClient = clientsQuery.data?.length === 1 ? clientsQuery.data[0] : null;

  /**
   * "What am I paying?" -- the other question this screen never answered (UX review F20).
   *
   * One line, not the itemised breakdown: that already exists on the settings page and belongs there.
   * What was missing was any sign of it at all on the page a parent actually opens, so Rob's "when does
   * the money leave" meant a tap into somewhere else to find out.
   */
  const billing = useFamilyBilling();

  /**
   * An athlete signing in is looking for "what do I do today", and was given credential administration
   * about herself instead: her own username, the paragraph about not being able to use "forgot
   * password", a Reset password button, and an invitation to add another athlete (UX review F54).
   * All of that is guardian business. She goes to her session.
   */
  if (isAthleteLogin && onlyClient) {
    return <Navigate to={`/family/clients/${onlyClient.id}/plan`} replace />;
  }

  return (
    // Settings and Sign out moved to the persistent nav bar.
    <PageShell title="Your training">
      <TourPrompt />

      {clientsQuery.isPending && (
        <>
          <LoadingState label="Loading your profiles…" />
          <SkeletonList rows={2} />
        </>
      )}

      {clientsQuery.isError && <ErrorState error={clientsQuery.error} onRetry={() => clientsQuery.refetch()} />}

      {clientsQuery.isSuccess && clientsQuery.data.length === 0 && <p className="empty-state">No profiles yet.</p>}

      {/* Quiet, and only once there is something true to say. A billing line that renders "—" while it
          loads, or an error box about money on the training screen, would both be worse than waiting. */}
      {!isAthleteLogin && billing.isSuccess && billing.data.monthlyTotalCents !== null && (
        <p className="family-billing-line">
          {describeMonthlyTotal(billing.data.monthlyTotalCents, billing.data.currency)}
          <Link className="link-button" to="/family/settings">
            Billing
          </Link>
        </p>
      )}

      <ul className="client-list">
        {clientsQuery.data?.map((client) => (
          <li key={client.id} className="client-card plan-card">
            <div className="plan-card-main">
              <span className="client-avatar">{initials(client.name)}</span>
              <span className="client-name">{client.name}</span>
              {/* Said in words, not in jargon. A bare grey "Read-only" badge was the only badge on the
                  screen, so it read as a warning, and a parent could not tell whether it described their
                  own access, their child's, or something being wrong (UX review F21). */}
              {!client.editable && (
                <span className="badge" title={`${client.name} logs their own sessions now. You can see everything.`}>
                  {client.name.split(" ")[0]} logs their own
                </span>
              )}
              <Link className="link-button" to={`/family/clients/${client.id}/plan`}>
                View plan
              </Link>
              <Link className="link-button" to={`/family/clients/${client.id}/messages`}>
                Messages
                {unreadClientIds.has(client.id) && <span className="nav-unread-dot" aria-label="Unread messages" />}
              </Link>
            </div>

            {/* The answer to "did they do it?", on the screen that question is asked on (F20/F66). */}
            <AthleteStatus clientId={client.id} />

            {/* Only for a minor who doesn't have one yet -- an adult self-profile already has its own
                login, and a minor who has one can't be given a second.
                Guarded on `!isAthleteLogin` as well, so that if the redirect above ever cannot fire
                (no client rows, or more than one) an athlete still never gets handed the credential
                administration for her own account. */}
            {!isAthleteLogin && client.is_minor && client.skater_login_id === null && (
              <SkaterLoginForm clientId={client.id} clientName={client.name} />
            )}

            {/* Once the login exists, the guardian needs the other half: what the username is, and the
                only way to reset a password nobody can email a link for (PRD §11.2). */}
            {!isAthleteLogin && client.is_minor && client.skater_login_id !== null && (
              <SkaterLoginDetails
                clientId={client.id}
                clientName={client.name}
                username={client.skater_login_username}
                createdAt={client.skater_login_created_at}
                passwordResetAt={client.skater_login_password_reset_at}
              />
            )}
          </li>
        ))}
      </ul>

      {/*
        No self-serve "add an athlete" here any more (finding #15). Who trains with the coach is the
        coach's decision, and each additional athlete is another subscription -- not something to start
        from a form on your own dashboard. Said out loud rather than silently removed, so a parent with
        a second skater knows what to do instead of concluding the app cannot do it.
      */}
      {/* Guardian business, and addressed to a guardian -- an athlete reading her own screen has no
          use for it and no way to act on it. */}
      {!isAthleteLogin && (
        <>
          <h2>Another athlete?</h2>
          <p className="form-hint">
            Message your coach and they&rsquo;ll add them. Each athlete has their own plans and their own
            subscription, so the coach sets them up.
          </p>
        </>
      )}
    </PageShell>
  );
}
